import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import { codeProposalResponseSchema, type CodeProposal } from './types';

// Propuesta de código del alta (G3-2): el servidor calcula el siguiente consecutivo del
// patrón de 04-GC-P08 para el tipo y el proceso elegidos (y el módulo en un registro SAP).
// Es una vista previa: el código definitivo lo asigna el alta bajo bloqueo.

export interface CodeProposalInput {
  documentTypeId: string;
  processAreaId: string;
  /** '' mientras no se elige (o el tipo no es SAP). */
  sapModule: string;
}

export function useCodeProposal(input: CodeProposalInput): UseQueryResult<CodeProposal> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  const { documentTypeId, processAreaId, sapModule } = input;
  return useQuery({
    queryKey: ['code-proposal', activeCompanyId, documentTypeId, processAreaId, sapModule],
    queryFn: async () => {
      const params = new URLSearchParams({ documentTypeId, processAreaId });
      if (sapModule !== '') {
        params.set('sapModule', sapModule);
      }
      const raw = await api.get<unknown>(
        `/api/v1/quality/documents/code-proposal?${params.toString()}`,
      );
      return codeProposalResponseSchema.parse(raw).data;
    },
    enabled: documentTypeId !== '' && processAreaId !== '' && activeCompanyId !== null,
    // Siempre fresca: otra alta puede tomar el consecutivo en cualquier momento.
    staleTime: 0,
    // Un rechazo (proceso sin tipo, módulo pendiente) es una respuesta, no una falla.
    retry: false,
  });
}
